import logger from '@eventbrite/client-logger';
import { fetchJSON } from '@eventbrite/http';
import { gettext } from '@eventbrite/i18n';
import { formatMajorMoney } from '@eventbrite/intl';
import { trackIntercomEvent } from '@eventbrite/marketing-tools-common';
import {
    DEPRECATED_BOOST_TRIAL_PACKAGE,
    EB_14_DAY_TRIAL_100_PACKAGE,
    EB_14_DAY_TRIAL_250_PACKAGE,
    EB_14_DAY_TRIAL_UNLIM_PACKAGE,
    EB_60_DAY_TRIAL_UNLIM_PACKAGE,
    TRIAL_STATUS_ACTIVE,
    VALID_14_DAY_TRIALS,
} from '../constants';
import {
    getPlaceFromRequest,
    getTimestampInSeconds,
    sendUpdateToIntercom,
} from '../utils';
import {
    PPTrial,
    PPTrialListResponse,
    StartPPTrialBody,
    StartPPTrialPayload,
    UpdatePPTrialPayload,
} from './types';

export * from './types';
export * from './utils';
export * from './__mocks__';

export const fetchPPTrial = async (
    organizationId: string,
): Promise<PPTrial | null> => {
    if (!organizationId) {
        throw new Error(
            '[fetchPPTrial]: organizationId is a required parameter.',
        );
    }

    let response = null;

    try {
        const { trials } = (await fetchJSON(
            `/api/v3/trials/organizations/${organizationId}/trials`,
            { method: 'GET' },
        )) as PPTrialListResponse;

        // There are two P&P trials (1 = regular, 2 = extended)
        // there's the possibility of an organization having the old boost trial (package_id === 0)
        // https://eventbrite.atlassian.net/browse/SUP-25520
        if (trials?.length === 1) {
            const trialObject = trials.find(
                (trial) =>
                    trial.package_id === EB_60_DAY_TRIAL_UNLIM_PACKAGE ||
                    trial.package_id === DEPRECATED_BOOST_TRIAL_PACKAGE ||
                    VALID_14_DAY_TRIALS.includes(trial.package_id),
            );

            if (trialObject) {
                response = trialObject;
            }
        } else if (trials?.length > 1) {
            const regularTrial = trials.find((trial) =>
                VALID_14_DAY_TRIALS.includes(trial.package_id),
            );
            const extendedTrial = trials.find(
                (trial) => trial.package_id === EB_60_DAY_TRIAL_UNLIM_PACKAGE,
            );

            const deprecatedBoostTrial = trials.find(
                (trial) => trial.package_id === DEPRECATED_BOOST_TRIAL_PACKAGE,
            );

            // extended/active trial takes precedence
            if (extendedTrial && extendedTrial.status === TRIAL_STATUS_ACTIVE) {
                response = extendedTrial;
            } else if (
                regularTrial &&
                regularTrial.status === TRIAL_STATUS_ACTIVE
            ) {
                response = regularTrial;
            } else if (extendedTrial) {
                response = extendedTrial;
            } else if (regularTrial) {
                response = regularTrial;
            } else if (deprecatedBoostTrial) {
                response = deprecatedBoostTrial;
            }
        }
    } catch (error) {
        logger.error(error, {
            message: `Error in fetchPPTrial for ${organizationId}`,
        });

        throw new Error(
            `There was an error fetching trial information for this organization. Please try again or contact support for assistance.`,
        );
    }

    return response;
};

export const isOrgEligibleForPPTrial = async (
    organizationId: string,
): Promise<boolean> => {
    if (!organizationId) {
        throw new Error(
            '[isOrgEligibleForPPTrial]: organizationId is a required parameter.',
        );
    }

    let response = true;

    try {
        const { trials } = (await fetchJSON(
            `/api/v3/trials/organizations/${organizationId}/trials`,
            { method: 'GET' },
        )) as PPTrialListResponse;

        if (trials && !!trials.length) {
            const activeExtendedTrial = trials.find(
                (trial) =>
                    trial.package_id === EB_60_DAY_TRIAL_UNLIM_PACKAGE &&
                    trial.status === TRIAL_STATUS_ACTIVE,
            );
            const trialsWithoutExtended = trials.filter(
                (trial) => trial.package_id !== EB_60_DAY_TRIAL_UNLIM_PACKAGE,
            );

            let active14DayTrial;
            if (trialsWithoutExtended.length > 0) {
                active14DayTrial = trialsWithoutExtended.find(
                    (trial) =>
                        VALID_14_DAY_TRIALS.includes(trial.package_id) &&
                        trial.status === TRIAL_STATUS_ACTIVE,
                );
            }

            if (activeExtendedTrial || !active14DayTrial) {
                response = false;
            }
        }
    } catch (error) {
        logger.error(error, {
            message: `Error in isOrgEligibleForPPTrial for ${organizationId}`,
        });
    }

    return response;
};

export const startPPTrial = async ({
    organizationId,
    planId,
    planPriceId,
    currency,
    nextChargeAmount,
    packageId,
    shouldAutoSub,
}: StartPPTrialPayload): Promise<PPTrial> => {
    if (!organizationId) {
        throw new Error(
            '[startPPTrial]: organizationId is a required parameter.',
        );
    }

    if (
        !planId ||
        typeof planId !== 'string' ||
        !planPriceId ||
        typeof planPriceId !== 'string' ||
        !currency ||
        typeof currency !== 'string'
    ) {
        logger.error('Invalid invocation of startPPTrial:', {
            planId,
            planPriceId,
            currency,
        });

        throw new Error(
            '[startPPTrial]: planId, planPriceId, and currency are required parameters and must be strings.',
        );
    }

    const jsonBody: StartPPTrialBody = {
        packageId:
            packageId?.toString() || EB_14_DAY_TRIAL_100_PACKAGE.toString(), // Phase 2.1 of P&P has a single trial (package_id === 1)
        planId: planId, // planId of the plan the org was shown when starting trial
        planPriceId: planPriceId, // planPriceId of the plan the org was shown when starting trial
        currency: currency, // currency of the plan the org was shown when starting trial
    };

    let isCreatorInDC = false;

    // We legally can't auto-sub DC creators; for now we're using IP address to auto-determine this
    try {
        const locationData = await getPlaceFromRequest();

        if (
            locationData?.place?.place_id === '85931779' ||
            locationData?.place?.parent_id === '102084889' ||
            locationData?.place?.parent_id === '1377370667'
        ) {
            isCreatorInDC = true;
        }
    } catch (error) {
        console.log('Error getting user location by IP');
    }

    if (isCreatorInDC || shouldAutoSub === false) {
        jsonBody.shouldAutoSub = 'N';
    } else if (shouldAutoSub) {
        jsonBody.shouldAutoSub = 'Y';
    }

    const body = JSON.stringify(jsonBody);

    let response;

    try {
        response = await fetchJSON(
            `/api/v3/trials/organizations/${organizationId}/trials`,
            { method: 'POST', body },
        );

        const eventbriteUser = window?.__SERVER_DATA__?.user;

        try {
            // TODO add more packages
            if (eventbriteUser) {
                let message = '';

                if (packageId === EB_14_DAY_TRIAL_100_PACKAGE.toString()) {
                    message = 'Pro 100 free';
                }

                if (packageId === EB_60_DAY_TRIAL_UNLIM_PACKAGE.toString()) {
                    message = 'Pro 100 free extended';
                }

                if (packageId === EB_14_DAY_TRIAL_250_PACKAGE.toString()) {
                    message = 'Pro 250 free';
                }

                if (packageId === EB_14_DAY_TRIAL_UNLIM_PACKAGE.toString()) {
                    message = 'Pro Unlimited free';
                }

                trackIntercomEvent(`PP pro free ${message} trial started`, {
                    email: eventbriteUser.email,
                });
            }

            // TO DO - don't hardcode all of these things
            // quick & dirty for now given launch strategy and PMM needs
            const intercomUpdateObject = {
                pp_trial_started_at: getTimestampInSeconds(response.created_at),
                pp_trial_will_end_at: getTimestampInSeconds(response.ends_at),
                pp_trial_type:
                    packageId === '2' || packageId === '4'
                        ? 'Pro Unlimited'
                        : packageId === '3'
                        ? 'Pro 250'
                        : 'Pro 100',
                pp_trial_status: 'active',
                pp_current_ticket_limit:
                    packageId === '2' || packageId === '4'
                        ? gettext('unlimited')
                        : packageId === '3'
                        ? '250'
                        : '100',
                pp_email_daily_send_limit:
                    packageId === '2' || packageId === '4'
                        ? 10000
                        : packageId === '3'
                        ? 6000
                        : 2000,
                pp_subscription_next_charge_amount:
                    packageId === '2' || shouldAutoSub === false
                        ? null
                        : formatMajorMoney(nextChargeAmount, currency),
                pp_subscription_next_charge_at:
                    packageId === '2' || shouldAutoSub === false
                        ? null
                        : getTimestampInSeconds(response.ends_at),
                pp_currency: response.currency,
                pp_is_creator_in_dc: isCreatorInDC,
            };

            sendUpdateToIntercom(intercomUpdateObject);
        } catch (error) {
            logger.error(error, {
                message: `Error sending update to Intercom for ${organizationId}`,
            });
        }

        return response as PPTrial;
    } catch (error) {
        logger.error(error, {
            message: `Error in startPPTrial for organization ${organizationId}`,
        });

        if (error.status === 409) {
            throw new Error(
                `This organization has an existing trial and cannot start a new trial. Please reach out to support for further assistance.`,
            );
        } else {
            throw new Error(
                `There was an issue starting your trial. Please try again or contact support for assistance.`,
            );
        }
    }
};

export const disableAutoSubscribeFromPPTrial = async ({
    organizationId,
    packageId,
}: {
    organizationId: string;
    packageId: string;
}): Promise<PPTrial> => {
    if (!organizationId) {
        throw new Error(
            '[disableAutoSubscribeFromPPTrial]: organizationId is a required parameter.',
        );
    }

    if (!packageId) {
        throw new Error(
            '[disableAutoSubscribeFromPPTrial]: packageId is a required parameter.',
        );
    }
    const body = JSON.stringify({
        shouldAutoSub: 'N',
    });

    try {
        const response = await fetchJSON(
            `/api/v3/trials/organizations/${organizationId}/trials/${packageId}`,
            { method: 'POST', body },
        );

        let trial;

        if (response) {
            trial = response.trial;

            const eventbriteUser = window?.__SERVER_DATA__?.user;

            if (eventbriteUser) {
                trackIntercomEvent('PP pro free trial canceled', {
                    email: eventbriteUser.email,
                });
            }

            const intercomUpdateObject = {
                pp_trial_canceled_at: getTimestampInSeconds('now'),
                pp_trial_status: 'canceled',
                pp_subscription_next_charge_amount: null,
                pp_subscription_next_charge_at: null,
            };

            sendUpdateToIntercom(intercomUpdateObject);
        }

        return trial;
    } catch (error) {
        logger.error(error, {
            message: `Error in disableAutoSubscribeFromPPTrial for organization ${organizationId}`,
        });

        throw new Error(
            `There was an issue disabling auto-subscribe for your trial. Please try again or contact support for further assistance.`,
        );
    }
};

export const enableAutoSubscribeFromPPTrial = async (
    organizationId: string,
): Promise<PPTrial> => {
    if (!organizationId) {
        throw new Error(
            '[enableAutoSubscribeFromPPTrial]: organizationId is a required parameter.',
        );
    }

    const body = JSON.stringify({
        shouldAutoSub: 'Y',
    });

    try {
        const response = await fetchJSON(
            `/api/v3/trials/organizations/${organizationId}/trials/1`,
            { method: 'POST', body },
        );

        let trial;

        if (response) {
            trial = response.trial;

            const intercomUpdateObject = {
                pp_trial_status: 'active',
            };

            sendUpdateToIntercom(intercomUpdateObject);
        }

        return trial;
    } catch (error) {
        logger.error(error, {
            message: `Error in enableAutoSubscribeFromPPTrial for organization ${organizationId}`,
        });

        throw new Error(
            `There was an issue re-enabling auto-subscribe for your trial.`,
        );
    }
};

export const updatePPTrial = async ({
    currentPackageId,
    payload,
}: {
    currentPackageId: string;
    payload: UpdatePPTrialPayload;
}): Promise<PPTrial> => {
    if (!payload.organizationId) {
        throw new Error(
            '[updatePPTrial]: organizationId is a required parameter.',
        );
    }

    const body = JSON.stringify({
        ...payload,
        shouldAutoSub: payload.shouldAutoSub ? 'Y' : 'N',
    });

    try {
        const response = await fetchJSON(
            `/api/v3/trials/organizations/${payload.organizationId}/trials/${currentPackageId}`,
            { method: 'POST', body },
        );

        return response?.trial as PPTrial;
    } catch (error) {
        logger.error(error, {
            message: `Error in updatePPTrial for organization ${payload.organizationId}`,
        });

        throw new Error(
            `There was an issue updating your trial. Please try again or contact support for further assistance.`,
        );
    }
};
